/**
 * Peças comuns das telas da Central de Projetos.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { useAuth } from "../../context/AuthContext";
import { lerMensagens } from "../../services/centralProjetos";
import { MensagensNaCaixa } from "./Mensagens";
import { Aviso, Botao, BotaoDiscreto, Vazio } from "./styles";

export const PERM = Object.freeze({
  VER: "central_projetos:view",
  MENSAGENS: "central_projetos:ver_mensagens",
  TRATAR: "central_projetos:tratar",
  RESPONDER: "central_projetos:responder",
  ASSISTENTE: "central_projetos:assistente",
  CONFIGURAR: "central_projetos:configurar",
});

/**
 * Carrega dados com o `request` e descarta resposta atrasada (troca de filtro).
 * `alterar(fn)` troca os dados já carregados sem ir ao servidor (ex.: o item
 * que um PATCH devolveu).
 */
export function useCarga(carregar, dependencias) {
  const { request } = useAuth();
  const [estado, setEstado] = useState({ dados: null, erro: null, carregando: true });
  const versao = useRef(0);

  const recarregar = useCallback(async () => {
    const minha = ++versao.current;
    setEstado((atual) => ({ ...atual, carregando: true }));
    try {
      const dados = await carregar(request);
      if (minha === versao.current) setEstado({ dados, erro: null, carregando: false });
    } catch (erro) {
      if (minha === versao.current) setEstado({ dados: null, erro, carregando: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencias);

  useEffect(() => {
    recarregar();
  }, [recarregar]);

  const alterar = useCallback((transformar) => {
    setEstado((atual) => (atual.dados ? { ...atual, dados: transformar(atual.dados) } : atual));
  }, []);

  return { ...estado, recarregar, alterar };
}

/** 409 = Central desligada na unidade. */
export function centralDesligada(erro) {
  return erro?.status === 409;
}

export function ErroDaCentral({ erro }) {
  const { hasPermission } = useAuth();
  if (centralDesligada(erro)) {
    return (
      <Aviso $tom="info">
        A Central de Projetos está desligada nesta unidade.{" "}
        {hasPermission?.(PERM.CONFIGURAR) ? (
          <Link to="/central-projetos/configuracao">Abrir a configuração</Link>
        ) : (
          "Peça a quem configura a Central para ligar."
        )}
      </Aviso>
    );
  }
  return <Aviso $tom="danger">{erro?.message || "Não foi possível carregar agora."}</Aviso>;
}

/**
 * Mensagens de origem (texto mascarado). Só para quem pode ler as conversas.
 * `rotulo` troca o texto do botão; `discreto`, o botão sem caixa (perfil).
 */
export function MensagensDeOrigem({ ids, rotulo, discreto = false }) {
  const { request, hasPermission } = useAuth();
  const [mensagens, setMensagens] = useState(null);
  const [erro, setErro] = useState(null);
  const podeLer = hasPermission?.(PERM.MENSAGENS);

  async function abrir() {
    setErro(null);
    try {
      const resposta = await lerMensagens(request, { ids: ids.join(",") });
      setMensagens(resposta.mensagens || []);
    } catch (e) {
      setErro(e?.message || "Não foi possível ler as mensagens.");
    }
  }

  if (!ids?.length) return null;
  if (!podeLer) return <Vazio as="span">{ids.length} mensagem(ns) de origem.</Vazio>;
  if (mensagens === null) {
    const Gatilho = discreto ? BotaoDiscreto : Botao;
    return (
      <>
        <Gatilho type="button" onClick={abrir}>
          {rotulo || `Ver ${ids.length === 1 ? "a mensagem de origem" : `as ${ids.length} mensagens de origem`}`}
        </Gatilho>
        {erro ? <Aviso $tom="danger">{erro}</Aviso> : null}
      </>
    );
  }
  return <MensagensNaCaixa mensagens={mensagens} mostrarAutores />;
}
